import { useEffect, useState } from "react";
import { Star, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import {
  useGoogleReviews,
  formatRatingSv,
  formatCountSv,
} from "../lib/googleReviews";

/**
 * Standard fallback-länk om Google Maps URI saknas från API:et.
 * (Ej hårdkodad live data — bara en säker fallback för länkknappen.)
 */
const GOOGLE_SEARCH_FALLBACK =
  "https://www.google.com/search?q=Tr%C3%A4df%C3%A4llarna+Laholm+AB";

/**
 * "Vad våra kunder säger" — live Google-omdömen.
 *
 * - Aggregate rating/count kommer från API (via GoogleReviewsContext).
 * - Endast recensioner med rating >= MIN_DISPLAYED_REVIEW_RATING visas
 *   (filtrering sker i providern). Om 0 kort returneras: dölj kort-griden
 *   men behåll rating-badge + länk till Google.
 * - Layout fungerar för 1–5 kort.
 */
export default function ReviewsSection({
  heading = "Vad våra kunder säger",
  subheading,
}) {
  const google = useGoogleReviews();

  // Om vi inte har någon data alls: gömmer hela sektionen istället för att
  // visa något halvfärdigt. (Behåller layout på övriga sidan.)
  if (!google.hasData) {
    return null;
  }

  const cards = google.reviews;
  const linkHref = google.googleMapsUri || GOOGLE_SEARCH_FALLBACK;

  return (
    <section
      data-testid="reviews-section"
      id="recensioner"
      className="section-pad bg-[#F7F5F0] scroll-mt-32"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 lg:mb-14">
          <div className="max-w-2xl">
            <span className="eyebrow">Recensioner</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1B2A1E] leading-tight tracking-tight">
              {heading}
            </h2>
            {subheading && (
              <p
                data-testid="reviews-subheading"
                className="mt-4 text-[#3a4338] text-base sm:text-lg leading-relaxed"
              >
                {subheading}
              </p>
            )}
          </div>
          <div
            data-testid="reviews-rating-box"
            className="flex items-center gap-4 bg-white border border-[#E2E0D8] rounded-xl px-5 py-3"
          >
            <div
              className="flex items-center gap-0.5"
              aria-label={`${formatRatingSv(google.rating)} av 5`}
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <Star
                  key={i}
                  className="w-5 h-5 fill-[#D95D2E] text-[#D95D2E]"
                  strokeWidth={1.5}
                />
              ))}
            </div>
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-[#1B2A1E] text-lg">
                <span data-testid="reviews-section-rating">
                  {formatRatingSv(google.rating)}
                </span>{" "}
                / 5
              </div>
              <div className="text-xs text-[#5A6258]">
                Baserat på{" "}
                <span data-testid="reviews-section-count">
                  {formatCountSv(google.ratingCount)}
                </span>{" "}
                Google-omdömen
              </div>
            </div>
          </div>
        </div>

        {cards.length > 0 ? (
          <ReviewsCarousel cards={cards} />
        ) : (
          <div
            data-testid="reviews-empty"
            className="bg-white border border-dashed border-[#E2E0D8] rounded-xl p-8 text-center text-[#5A6258] text-sm max-w-2xl mx-auto"
          >
            Vill du läsa vad tidigare kunder tycker om oss? Följ länken nedan
            till våra samlade omdömen på Google.
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <a
            href={linkHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-google-link"
            className="inline-flex items-center gap-2 bg-[#1B2A1E] hover:bg-[#283224] text-white font-semibold px-5 py-3 rounded-lg transition-colors"
          >
            Läs fler recensioner på Google
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function ReviewCard({ r, index }) {
  const rating = Number(r.rating || 0);
  return (
    <article
      data-testid={`review-card-${index + 1}`}
      className="h-full bg-white border border-[#E2E0D8] rounded-xl p-6 flex flex-col"
    >
      <div
        className="flex items-center gap-0.5 mb-3"
        aria-label={`${rating} av 5`}
      >
        {Array.from({ length: 5 }).map((_, idx) => (
          <Star
            key={idx}
            className={`w-4 h-4 ${
              idx < rating
                ? "fill-[#D95D2E] text-[#D95D2E]"
                : "text-[#E2E0D8]"
            }`}
            strokeWidth={1.5}
          />
        ))}
      </div>
      <p className="text-[#18221A] leading-relaxed mb-5 flex-1">
        “{r.text}”
      </p>
      <div className="flex items-center justify-between pt-4 border-t border-[#E2E0D8] gap-3">
        <div className="min-w-0 flex items-center gap-3">
          {r.author_photo_uri ? (
            <img
              src={r.author_photo_uri}
              alt=""
              className="w-8 h-8 rounded-full object-cover shrink-0"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          ) : null}
          <div className="min-w-0">
            <div className="font-heading font-bold text-[#1B2A1E] text-sm truncate">
              {r.author_uri ? (
                <a
                  href={r.author_uri}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D95D2E]"
                >
                  {r.author_name}
                </a>
              ) : (
                r.author_name
              )}
            </div>
            <div className="text-xs text-[#5A6258]">
              {[r.relative_time, "Google"].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="w-5 h-5 shrink-0"
        >
          <path
            fill="#4285F4"
            d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.227c1.887-1.74 2.986-4.302 2.986-7.35Z"
          />
          <path
            fill="#34A853"
            d="M12 22c2.7 0 4.964-.895 6.614-2.422l-3.227-2.51c-.895.6-2.04.954-3.387.954-2.604 0-4.81-1.76-5.595-4.122H3.064v2.59A9.997 9.997 0 0 0 12 22Z"
          />
          <path
            fill="#FBBC05"
            d="M6.405 13.9a6.01 6.01 0 0 1 0-3.8V7.51H3.064a10.01 10.01 0 0 0 0 8.98l3.341-2.59Z"
          />
          <path
            fill="#EA4335"
            d="M12 5.977c1.469 0 2.787.505 3.823 1.495l2.868-2.868C16.96 2.99 14.697 2 12 2 8.077 2 4.69 4.255 3.064 7.51l3.341 2.59C7.19 7.737 9.396 5.977 12 5.977Z"
          />
        </svg>
      </div>
    </article>
  );
}


/**
 * Carousel för recensionskort. Visar 3 kort per sida på ≥lg, 2 på md och
 * 1 på mobil. Pilar bläddrar ett kort i taget. Pilarna döljs om alla får plats.
 */
function ReviewsCarousel({ cards }) {
  const [visible, setVisible] = useState(3);
  const [start, setStart] = useState(0);

  // Responsiv "hur många kort som får plats" — MediaQuery via window.
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w >= 1024) setVisible(3);
      else if (w >= 768) setVisible(2);
      else setVisible(1);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, []);

  // Om vi krymper viewporten kan start hamna för långt fram.
  useEffect(() => {
    const max = Math.max(0, cards.length - visible);
    if (start > max) setStart(max);
  }, [visible, cards.length, start]);

  const maxStart = Math.max(0, cards.length - visible);
  const showArrows = cards.length > visible;
  const canPrev = start > 0;
  const canNext = start < maxStart;

  // 100% / visible per kort — jämnt fördelat.
  const cardWidthPct = 100 / visible;
  const translateXPct = -(start * cardWidthPct);

  return (
    <div
      data-testid="reviews-carousel"
      className="relative"
      aria-roledescription="carousel"
      aria-label="Kundomdömen"
    >
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-out will-change-transform"
          style={{ transform: `translate3d(${translateXPct}%, 0, 0)` }}
        >
          {cards.map((r, i) => (
            <div
              key={i}
              className="shrink-0 px-2 lg:px-3"
              style={{ flexBasis: `${cardWidthPct}%` }}
              aria-hidden={i < start || i >= start + visible ? "true" : "false"}
            >
              <ReviewCard r={r} index={i} />
            </div>
          ))}
        </div>
      </div>

      {showArrows && (
        <>
          <button
            type="button"
            onClick={() => setStart((s) => Math.max(0, s - 1))}
            disabled={!canPrev}
            data-testid="reviews-carousel-prev"
            aria-label="Föregående omdöme"
            className="absolute left-0 sm:-left-3 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-[#1B2A1E] shadow-md border border-[#E2E0D8] hover:border-[#D95D2E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={2} />
          </button>
          <button
            type="button"
            onClick={() => setStart((s) => Math.min(maxStart, s + 1))}
            disabled={!canNext}
            data-testid="reviews-carousel-next"
            aria-label="Nästa omdöme"
            className="absolute right-0 sm:-right-3 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-11 h-11 rounded-full bg-white text-[#1B2A1E] shadow-md border border-[#E2E0D8] hover:border-[#D95D2E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={2} />
          </button>
        </>
      )}

      {showArrows && (
        <div className="mt-6 flex justify-center gap-1.5" aria-hidden="true">
          {Array.from({ length: maxStart + 1 }).map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStart(i)}
              data-testid={`reviews-carousel-dot-${i}`}
              aria-label={`Visa omdöme-grupp ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === start
                  ? "w-6 bg-[#D95D2E]"
                  : "w-1.5 bg-[#C9C6BC] hover:bg-[#5A6258]"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
